/**
 * Storing the replacement document a recurring-message edit creates (#1133).
 *
 * Editing or dragging one occurrence out of a series ("only this one", "this and
 * following") stores a NEW document for it, built from the stored series document. Two
 * things made that store fail silently, and the plan then excluded the occurrence from the
 * series anyway — so it left the series and arrived nowhere:
 *
 *  1. The series document carries `parentMessageId`, the link the backend's next-occurrence
 *     writer stamps on every successor. The backend allows ONE live document per
 *     `parentMessageId` (partial unique index `parentMessageId_1_unique_live`), and the
 *     series already holds it, so the inherited link made the create fail with E11000 on
 *     every series that had advanced at least once. A split-off occurrence is a new chain
 *     root, not the series' successor, so it must not carry the link.
 *
 *  2. The generic DB `create` thunk (DBServiceReducers) catches a backend failure and
 *     FULFILLS with the error as its payload. `.unwrap()` therefore never throws, and
 *     applyRecurringEditPlan — whose contract is "createReplacement must reject if it did
 *     not store" — went on to patch or delete the original. This module enforces that
 *     contract locally; changing the shared thunk is a wider change, tracked separately.
 */

export interface StoredDocument {
  _id: string;
}

/** Returns a copy of `doc` without the series chain link (`parentMessageId`). */
export function withoutSeriesChainLink<T extends object>(
  doc: T & { parentMessageId?: unknown },
): Omit<T & { parentMessageId?: unknown }, 'parentMessageId'> {
  const { parentMessageId: _seriesLink, ...replacement } = doc;
  return replacement;
}

function isStoredDocument(result: unknown): result is StoredDocument {
  if (typeof result !== 'object' || result === null || result instanceof Error) return false;
  if (!('_id' in result)) return false;
  const { _id } = result;
  return typeof _id === 'string' && _id.length > 0;
}

/** A single-recipient edit stores one document; a multi-recipient edit fans out to one per recipient. */
export type StoredReplacement = StoredDocument | StoredDocument[];

function isStoredReplacement(result: unknown, expectedCount: number | undefined): result is StoredReplacement {
  if (Array.isArray(result)) {
    // Every recipient must have stored. A swallowed per-recipient create sits in its slot as
    // the error payload; a REJECTED one is dropped by fanOutScheduleRecipients, so the array
    // comes back shorter than the recipient list. Either way, patching the series would drop
    // that recipient's occurrence; refusing leaves at worst a duplicate for the others, which
    // the customer can see and fix.
    const countMatches = expectedCount === undefined ? result.length > 0 : result.length === expectedCount;
    return countMatches && result.every(isStoredDocument);
  }
  if (expectedCount !== undefined && expectedCount > 1) return false;
  return isStoredDocument(result);
}

/**
 * Throws unless `result` is a stored replacement. What the DB create thunk hands back on a
 * backend failure — the error payload, an Error, or `undefined` from its auth check — has
 * no `_id`. `expectedCount` is the number of recipients: a multi-recipient replacement must
 * come back with exactly one stored document per recipient.
 */
export function assertReplacementStored(result: unknown, expectedCount?: number): asserts result is StoredReplacement {
  if (!isStoredReplacement(result, expectedCount)) {
    throw new Error('[recurring-edit] replacement was not stored; leaving the original series untouched');
  }
}

/**
 * Stores a recurring edit's replacement and resolves only once it is really stored, so
 * applyRecurringEditPlan never removes or rewrites the original for a replacement that
 * does not exist.
 */
export async function storeRecurringReplacement<T extends object>(
  replacement: T & { parentMessageId?: unknown; contactList?: readonly unknown[] },
  persist: (doc: Omit<T & { parentMessageId?: unknown; contactList?: readonly unknown[] }, 'parentMessageId'>) => Promise<unknown>,
): Promise<StoredReplacement> {
  const stored = await persist(withoutSeriesChainLink(replacement));
  assertReplacementStored(stored, replacement.contactList?.length);
  return stored;
}
